import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'
import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Recognition › Gaps (`rc-gaps`), from `GET /v1/admin/recognition/gaps`.
 *
 * # The corpus screen was wrong about what a gap IS, not merely about its rows
 *
 * It described *"tracked gaps where a platform is known to exist but no signal
 * reaches it"* and drew a **Platform** column with TikTok, Meta, Streamlabs and
 * Twitch in it. The backend:
 *
 * > *"THERE IS NO PER-PLATFORM VIEW OF THIS REGISTER, AND THERE CANNOT BE. A
 * > gap is one CREDIT with no recognition decision, and the defining property
 * > of these rows is that no platform was identified — so grouping them by
 * > platform is not a harder query, it is a category error."*
 *
 * So the unit of this register is a **credit**, not a platform, and the
 * per-platform question is `GET /v1/admin/recognition/coverage`'s — over the
 * credits that *were* decided. There is no Platform column below.
 *
 * # `signals` is which inputs arrived, never what they said
 *
 * The payer name, IBAN and remittance strings are deliberately absent from the
 * response: this surface is pseudonymous throughout, and *"joining the raw
 * strings in to make the screen friendlier is exactly what the schema
 * constraint on `recognition_feedback.payer_key` exists to prevent."* An
 * operator who needs them goes to `GET /v1/admin/movements` under `ledger.read`.
 *
 * # An erasure reports every signal false, and that is not the institution's fault
 *
 * The trap the five booleans walk into on their own. An Art. 17 erasure clears
 * those columns, so a redacted row reports nothing arrived — which, rendered as
 * *"the institution sent nothing"*, is a false statement about the institution
 * and points an operator at the wrong party. `counterparty_redacted` is what
 * keeps the two apart, and this module renders them as different sentences
 * because they have opposite next actions: the first is a payer worth raising a
 * catalog candidate about, the second is a row nobody can act on any further.
 *
 * # Read only, and every control the design implies is refused by name
 *
 * No requeue, no dismiss, no escalate, no assignee, no note. Requeue and
 * escalation are the reconciler's; a dismiss *"would be an operator declaring a
 * credit not worth attributing, which is a decision about somebody's money with
 * no authorisation seam to make it through"*; and nothing in the schema records
 * who owns a gap, so *"a name rendered beside one would be invented"*.
 *
 * A gap leaves this register by being **solved** — the credit gains a decision
 * and the row is deleted — and by no other route.
 */

export const RECOGNITION_GAPS_PATH = '/recognition/gaps'

const SOURCE = 'postgres · recognition_gaps (GET /v1/admin/recognition/gaps)'
const AUTHORITY = 'operational'

interface Signals {
  readonly payerName: boolean
  readonly payerIBAN: boolean
  readonly remittanceUnstructured: boolean
  readonly remittanceStructured: boolean
  readonly endToEndID: boolean
  readonly counterpartyRedacted: boolean
}

interface Gap {
  readonly id: number | null
  readonly providerEventId: number | null
  readonly provider: string
  readonly amountMinor: number | null
  readonly currency: string
  readonly valueDate: string
  readonly postedAt: string
  /** Absent when the account names nobody. Never zero. */
  readonly customerId: number | null
  readonly firstSeenAt: string
  readonly lastSeenAt: string
  readonly requeues: number | null
  /** SERVED as a boolean as well as an instant, and always — including false. */
  readonly escalated: boolean | null
  readonly escalatedAt: string
  readonly signals: Signals
}

function readSignals(raw: unknown): Signals {
  const o = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}
  const flag = (k: string): boolean => o[k] === true
  return {
    payerName: flag('payer_name'),
    payerIBAN: flag('payer_iban'),
    remittanceUnstructured: flag('remittance_unstructured'),
    remittanceStructured: flag('remittance_structured'),
    endToEndID: flag('end_to_end_id'),
    counterpartyRedacted: flag('counterparty_redacted'),
  }
}

function readGap(raw: unknown): Gap | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const id = int(o['id'])
  if (id === null) return null
  const money = typeof o['amount'] === 'object' && o['amount'] !== null
    ? (o['amount'] as Record<string, unknown>)
    : {}
  return {
    id,
    providerEventId: int(o['provider_event_id']),
    provider: str(o['provider']),
    amountMinor: int(money['amount_minor']),
    currency: str(money['currency']),
    valueDate: str(o['value_date']),
    postedAt: str(o['posted_at']),
    customerId: int(o['customer_id']),
    firstSeenAt: str(o['first_seen_at']),
    lastSeenAt: str(o['last_seen_at']),
    requeues: int(o['requeues']),
    escalated: typeof o['escalated'] === 'boolean' ? o['escalated'] : null,
    escalatedAt: str(o['escalated_at']),
    signals: readSignals(o['signals']),
  }
}

/**
 * The columns. There is no Platform column, and there cannot be one.
 *
 * See the module header: the defining property of every row here is that no
 * platform was identified.
 */
const COLS: readonly Column[] = [
  ['Credit', 'm', '96px'],
  ['Institution', 'm', '120px'],
  ['Amount', 'a', '150px'],
  ['Value date', 'm', '116px'],
  ['Landed in', 'm', '130px'],
  ['What arrived with it', 'x', 'minmax(240px,1.8fr)'],
  ['Waiting since', 'm', '120px'],
  ['Requeues', 'n', '100px'],
  ['Escalated', 'c', '150px'],
]

/** The five inputs, in the order the response declares them. */
const SIGNAL_LABELS: readonly (readonly [key: keyof Signals, label: string])[] = [
  ['payerName', 'payer name'],
  ['payerIBAN', 'payer IBAN'],
  ['remittanceUnstructured', 'remittance'],
  ['remittanceStructured', 'structured remittance'],
  ['endToEndID', 'end-to-end id'],
]

/**
 * Which recognition inputs arrived — and the branch that keeps an erasure from
 * being read as an institution that sent nothing.
 *
 * The two states produce the same five booleans and mean opposite things, so
 * they get different sentences rather than a shared one with a flag beside it.
 * A redacted row is checked FIRST: whatever those columns now say about it is
 * not evidence about the institution, because we are the ones who cleared them.
 */
function signalCell(g: Gap): Cell {
  if (g.signals.counterpartyRedacted) {
    return 'WE ERASED THIS — the inputs are gone because of an Art. 17 request, not because none arrived'
  }
  const present = SIGNAL_LABELS.filter(([key]) => g.signals[key]).map(([, label]) => label)
  if (present.length === 0) {
    return 'nothing to recognise it by — the institution sent no payer name, IBAN or remittance'
  }
  return present.join(' · ')
}

function escalatedCell(g: Gap): ChipCell {
  if (g.escalated === null) return ['— not stated', 'r']
  if (!g.escalated) return ['not escalated', 'n']
  return [g.escalatedAt === '' ? 'escalated' : `escalated ${day(g.escalatedAt)}`, 'r']
}

/**
 * The amount, and the one thing law 8 forbids.
 *
 * A minor-unit figure with no currency is not rendered as a number. It is
 * withheld and said to be missing — the same posture the typology catalogue
 * takes on a threshold whose currency did not arrive.
 */
function amountCell(g: Gap): Cell {
  if (g.amountMinor === null || g.currency === '') return '— no amount with a currency'
  return [g.amountMinor, g.currency] as const
}

function row(g: Gap): readonly Cell[] {
  return [
    g.providerEventId === null ? `gap ${String(g.id)}` : `#${String(g.providerEventId)}`,
    g.provider === '' ? '— not stated' : g.provider,
    amountCell(g),
    g.valueDate === '' ? '—' : g.valueDate,
    // Absent, not zero: the credit landed somewhere, and "customer 0" would
    // send somebody looking for an account that is not missing.
    g.customerId === null ? '— account names nobody' : String(g.customerId),
    signalCell(g),
    g.firstSeenAt === '' ? '—' : day(g.firstSeenAt),
    g.requeues === null ? '—' : String(g.requeues),
    escalatedCell(g),
  ]
}

/** The four constant notes, plus the derived empty note. */
const REQUIRED_NOTES: readonly (readonly [key: string, kicker: string, tone: 'a' | 'n' | 'r'])[] = [
  ['empty_note', 'What this register holds', 'a'],
  ['signal_note', 'Which inputs arrived, never what they said', 'n'],
  ['redaction_note', 'An erasure is not an institution that sent nothing', 'r'],
  ['platform_note', 'There is no per-platform view, and there cannot be', 'n'],
  ['control_note', 'Read only', 'n'],
]

export function composeRecognitionGaps(
  payload: Record<string, unknown>, readAt: Date,
): ScreenData {
  const raw = Array.isArray(payload['gaps']) ? (payload['gaps'] as readonly unknown[]) : []
  const rows: Gap[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readGap(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const hasMore = payload['has_more'] === true
  const redacted = rows.filter((g) => g.signals.counterpartyRedacted).length
  const blind = rows.filter(
    (g) => !g.signals.counterpartyRedacted && SIGNAL_LABELS.every(([key]) => !g.signals[key]),
  ).length

  const table: TableBlock = {
    t: 'table',
    title: 'Credits with no recognition decision',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} credit(s)${hasMore ? ' — and more beyond this page' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'One row is one credit, not one platform: what these credits have in common is that no platform was identified for any of them. A gap leaves this register by being solved — the credit gains a decision and the row goes — and by no other route.',
  }

  const blocks: Block[] = [table]

  if (hasMore) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Not the whole register',
      title: `This page is cut short — ${String(rows.length)} credit(s) shown, and there are more`,
      body: 'The backend says so on the response; this console does not infer it from the page being full, which would be a second implementation of the bound. Working this list to its end and finding nothing more would otherwise read as there being nothing more.',
      ref: 'has_more is served on every response, true or false',
    })
  }

  if (blind > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Nothing to recognise them by',
      title: `${String(blind)} credit(s) arrived with none of the five recognition inputs`,
      body: 'No payer name, no IBAN, no remittance in either form, no end-to-end id. These are the ones where the institution genuinely sent nothing to attribute the money by — which is a payer worth raising a catalog candidate about, and is a different finding from a row we erased ourselves.',
      ref: 'every signal false, and counterparty_redacted false',
    })
  }

  if (redacted > 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Erased',
      title: `${String(redacted)} credit(s) report no inputs because WE cleared them`,
      body: 'An Art. 17 erasure clears the counterparty columns, so those rows report every signal absent. That says nothing about what the institution sent, and reading it as "they sent nothing" points at the wrong party. There is nothing further anybody can do with these — they are not a gap in the catalog, they are a gap in what we are permitted to still hold.',
      ref: 'counterparty_redacted is true on those rows',
    })
  }

  for (const [key, kicker, tone] of REQUIRED_NOTES) {
    const body = str(payload[key])
    if (body !== '') {
      blocks.push({ t: 'note', tone, kicker, title: 'From the backend, verbatim', body, ref: key })
    }
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} gap row(s) could not be read`,
      body: 'Counted rather than dropped silently. A gap register quietly one credit shorter than the record is the failure this screen exists to surface, arriving through the console instead of through the pipeline.',
      ref: 'the count is the finding',
    })
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (hasMore) missing.push('the rest of the register beyond this page')
  // Always. Withheld by design rather than unavailable, and an operator who
  // needs them has a route — which is more use than a blank column.
  missing.push('payer name, IBAN and remittance strings — withheld here, served by GET /v1/admin/movements')
  missing.push('who owns a gap — nothing in the schema records an assignee')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  // Credits, not platforms — the unit is stated in the subject line too,
  // because the screen this replaces counted the wrong thing.
  const subject = `recognition_gaps · ${String(rows.length)} credit(s) read${hasMore ? ' of more' : ''}`
  return { blocks, provenance, subject }
}

export const loadRecognitionGaps: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(RECOGNITION_GAPS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The recognition-gap register answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The gap register returned a shape this console does not know.', null)
  }
  return composeRecognitionGaps(payload as Record<string, unknown>, new Date())
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The recognition-gap register could not be read',
      body: `${reason} Nothing is drawn. In particular this is NOT an empty register: whether any credit is sitting unattributed is unknown from here, and an empty register would not have meant that either — this table is written by the D2 sweep, so one that never ran leaves it empty too.`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
